import { DEFAULT_TENANT_CAPABILITIES, type TenantCapabilities } from '@/shared/api/tenantSettings';

/**
 * Index of the administration area (13 §3.3, §4.12): eight groups, every configurable catalog.
 * `implemented` marks the pages that exist; the rest render as "próximamente" in the admin menu
 * and on the index cards. Every entry requires `admin.access`; a page may demand more.
 */
export interface AdminEntry {
  labelKey: string;
  to: string;
  implemented: boolean;
  /** Permission the page itself requires beyond `admin.access`; with several, any one of them is enough. */
  permission?: string | readonly string[];
  /** `AM-204`: the entry disappears outright, unlike a missing permission, while this capability is off. */
  capability?: 'commercialBilling';
  /**
   * Points at another entry's page. The index lists it; the side menu leaves it out, since the
   * two links would light up together.
   */
  alias?: boolean;
}

export interface AdminGroup {
  key: string;
  entries: readonly AdminEntry[];
}

export const ADMIN_GROUPS: readonly AdminGroup[] = [
  {
    key: 'people',
    entries: [
      {
        labelKey: 'users',
        to: '/admin/users',
        implemented: true,
        permission: 'admin.users.manage',
      },
      {
        labelKey: 'roles',
        to: '/admin/roles',
        implemented: true,
        permission: 'admin.roles.manage',
      },
      {
        labelKey: 'teams',
        to: '/admin/teams',
        implemented: true,
        permission: 'admin.teams.manage',
      },
      {
        labelKey: 'agentProfiles',
        to: '/admin/agent-profiles',
        implemented: true,
        permission: 'admin.teams.manage',
      },
      {
        labelKey: 'technicianRoles',
        to: '/admin/technician-roles',
        implemented: true,
        permission: 'admin.teams.manage',
      },
    ],
  },
  {
    key: 'organization',
    entries: [
      {
        labelKey: 'organizations',
        to: '/admin/organizations',
        implemented: true,
        permission: 'admin.organizations.manage',
      },
      {
        labelKey: 'departments',
        to: '/admin/departments',
        implemented: true,
        permission: 'admin.persons.manage',
      },
      {
        labelKey: 'locations',
        to: '/admin/locations',
        implemented: true,
        permission: 'admin.organizations.manage',
      },
      {
        labelKey: 'persons',
        to: '/admin/persons',
        implemented: true,
        permission: 'admin.persons.manage',
      },
      {
        labelKey: 'vendors',
        to: '/admin/vendors',
        implemented: true,
        permission: 'admin.organizations.manage',
      },
    ],
  },
  {
    key: 'catalog',
    entries: [
      {
        labelKey: 'services',
        to: '/admin/services',
        implemented: true,
        permission: 'admin.services.manage',
      },
      {
        labelKey: 'serviceAreas',
        to: '/admin/service-areas',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
      // `AM-219`: the recurring work of each rubro, next to the rubros themselves.
      {
        labelKey: 'maintenancePlans',
        to: '/admin/maintenance-plans',
        implemented: true,
        permission: 'maintenance_plans.manage',
      },
      {
        labelKey: 'serviceOfferings',
        to: '/admin/service-offerings',
        implemented: true,
        permission: 'admin.services.manage',
      },
      {
        labelKey: 'categories',
        to: '/admin/categories',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
      {
        labelKey: 'priorities',
        to: '/admin/priorities',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
      {
        labelKey: 'taskTypes',
        to: '/admin/task-types',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
    ],
  },
  {
    key: 'assets',
    entries: [
      {
        labelKey: 'assetTypes',
        to: '/admin/asset-types',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
      {
        labelKey: 'ciTypes',
        to: '/admin/ci-types',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
    ],
  },
  {
    key: 'workflow',
    entries: [
      {
        labelKey: 'statuses',
        to: '/admin/statuses',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
      // The reasons of a status are edited inside its own panel (05b §7.2): the index entry of
      // 13 §3.3 points at the same page instead of a list that the API does not expose.
      {
        labelKey: 'statusReasons',
        to: '/admin/statuses',
        implemented: true,
        permission: 'admin.catalog.manage',
        alias: true,
      },
      {
        labelKey: 'transitions',
        to: '/admin/transitions',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
      {
        labelKey: 'automation',
        to: '/admin/automation',
        implemented: true,
        permission: 'admin.automation.manage',
      },
      {
        labelKey: 'approvalRules',
        to: '/admin/approval-rules',
        implemented: true,
        permission: 'admin.approvals.manage',
      },
    ],
  },
  {
    key: 'serviceLevel',
    entries: [
      {
        labelKey: 'slaPolicies',
        to: '/admin/sla-policies',
        implemented: true,
        permission: 'admin.sla.manage',
      },
      {
        labelKey: 'businessHours',
        to: '/admin/business-hours',
        implemented: true,
        permission: 'admin.calendars.manage',
      },
      {
        labelKey: 'holidays',
        to: '/admin/holidays',
        implemented: true,
        permission: 'admin.calendars.manage',
      },
    ],
  },
  {
    // `AM-491`: "Facturación y horas" (it was "Tiempo y facturación"), in the order of the model:
    // Opciones generales (it was "Sistema > Facturación y horas"), Tipos de trabajo, Reglas de
    // facturación, Alícuotas de IVA and Numeración de comprobantes. `AM-511`: "Datos fiscales del
    // emisor" right after Opciones generales, open also to whoever configures the billing. `AM-812`
    // (decision 13 of the 52): the other sequences stay in "Secuencias de numeración", last of the group.
    key: 'billingAndHours',
    entries: [
      {
        labelKey: 'capabilities',
        to: '/admin/capabilities',
        implemented: true,
        permission: 'admin.tenant.configure',
      },
      {
        labelKey: 'fiscalIssuer',
        to: '/admin/fiscal-issuer',
        implemented: true,
        permission: ['admin.tenant.configure', 'admin.billing.configure'],
      },
      {
        labelKey: 'workTypes',
        to: '/admin/work-types',
        implemented: true,
        permission: 'admin.catalog.manage',
      },
      {
        labelKey: 'billingRules',
        to: '/admin/billing-rules',
        implemented: true,
        permission: 'admin.billing.configure',
        capability: 'commercialBilling',
      },
      {
        labelKey: 'taxRates',
        to: '/admin/tax-rates',
        implemented: true,
        permission: 'admin.billing.configure',
        capability: 'commercialBilling',
      },
      {
        labelKey: 'invoiceNumbering',
        to: '/admin/invoice-numbering',
        implemented: true,
        permission: 'admin.billing.configure',
        capability: 'commercialBilling',
      },
      {
        labelKey: 'sequences',
        to: '/admin/sequences',
        implemented: true,
        permission: 'admin.tenant.configure',
      },
    ],
  },
  {
    key: 'communication',
    entries: [
      {
        labelKey: 'notificationTemplates',
        to: '/admin/notifications',
        implemented: true,
        permission: 'admin.notifications.manage',
      },
      {
        labelKey: 'notificationRules',
        to: '/admin/notification-rules',
        implemented: true,
        permission: 'admin.notifications.manage',
      },
      {
        labelKey: 'notificationDeliveries',
        to: '/admin/notification-deliveries',
        implemented: true,
        permission: 'admin.notifications.manage',
      },
    ],
  },
  {
    key: 'system',
    entries: [
      {
        labelKey: 'branding',
        to: '/admin/branding',
        implemented: true,
        permission: 'admin.tenant.configure',
      },
      {
        labelKey: 'security',
        to: '/admin/security',
        implemented: true,
        permission: 'admin.tenant.configure',
      },
      { labelKey: 'audit', to: '/admin/audit', implemented: true, permission: 'audit.view' },
      {
        labelKey: 'dataExport',
        to: '/admin/data-export',
        implemented: true,
        permission: 'admin.tenant.export',
      },
      // "Funciones habilitadas" (`/admin/feature-flags`, read-only) stays out of the tenant's console
      // until some module reads a flag: today none does, so the screen showed values with no effect.
      // `FeatureFlagsPage` keeps the page; its entry here and its route come back together.
      {
        labelKey: 'supportAccess',
        to: '/admin/support-access',
        implemented: true,
        permission: 'admin.support_access.approve',
      },
    ],
  },
];

export function canOpen(entry: AdminEntry, permissions: ReadonlySet<string>): boolean {
  const required =
    entry.permission === undefined
      ? []
      : typeof entry.permission === 'string'
        ? [entry.permission]
        : entry.permission;
  return (
    permissions.has('admin.access') &&
    (required.length === 0 || required.some((key) => permissions.has(key)))
  );
}

/**
 * `AM-204`: whether the tenant's own capabilities make this entry available at all. Unlike a
 * missing permission - which still lists the entry, greyed out, so an administrator knows the
 * catalogue exists - a masked capability drops the entry outright, the same as the main nav.
 */
export function entryAvailable(
  entry: AdminEntry,
  capabilities: TenantCapabilities = DEFAULT_TENANT_CAPABILITIES,
): boolean {
  return entry.capability !== 'commercialBilling' || capabilities.commercialBillingEnabled;
}
